import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useLocationUrlSync } from '@/contexts/LocationContext';
import { formatDateNL, formatHoursHM, getISOWeek, getISOWeekOffset, isoWeekDates, navigateIsoWeek } from '@/lib/format';
import {
  CalendarDays, CalendarCheck, ChevronLeft, ChevronRight, RefreshCw, Trash2, AlertTriangle, Flag, Undo2, Wifi, WifiOff, Sparkles, MessageSquare, Loader2, Grid3X3, Gauge, CheckCircle2, Circle, Users,
} from 'lucide-react';
import AIChatPanel from '@/components/AIChatPanel';
import ReasoningPopover from '@/components/ReasoningPopover';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const DAYS = [
  { dow: 1, short: 'Ma' },
  { dow: 2, short: 'Di' },
  { dow: 3, short: 'Wo' },
  { dow: 4, short: 'Do' },
  { dow: 5, short: 'Vr' },
  { dow: 6, short: 'Za' },
  { dow: 7, short: 'Zo' },
];

const POLL_INTERVAL_MS = 30000;
const UNDO_TOAST_MS = 10000;

export default function Roosterplanner() {
  const { user } = useAuth();
  const { slug: selectedSlug, setSlug: setSelectedSlug } = useLocationUrlSync();
  const [locations, setLocations] = useState([]);
  const initialWeek = getISOWeekOffset(1); // standaard op VOLGENDE week (plannen vooruit)
  const [year, setYear] = useState(initialWeek.year);
  const [week, setWeek] = useState(initialWeek.week);
  const [employees, setEmployees] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [etag, setEtag] = useState('');
  const [editingCell, setEditingCell] = useState(null);
  const [feedback, setFeedback] = useState({ type: '', msg: '' });
  const [conflictNotice, setConflictNotice] = useState(false);
  const [undoState, setUndoState] = useState(null); // {assignment, expiresAt}
  const [lastUpdate, setLastUpdate] = useState(null);
  const [livePollOk, setLivePollOk] = useState(true);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiFailedAt, setAiFailedAt] = useState(null);          // Timestamp of last AI failure → enables standaardrooster fallback
  const [templateRolloutBusy, setTemplateRolloutBusy] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatContext, setChatContext] = useState(null);
  const [reasoningOpen, setReasoningOpen] = useState(null); // {assignmentId, anchorRect}
  const [usage, setUsage] = useState(null);
  const [kpuById, setKpuById] = useState({});
  const [omzetByDay, setOmzetByDay] = useState({});
  const [omzetSources, setOmzetSources] = useState({}); // date -> handmatig|forecast|dailyops|geen
  const [omzetDetail, setOmzetDetail] = useState({});   // date -> {base, weather_factor, weather_reason, season_factor}
  const [pkDoel, setPkDoel] = useState({ default_pct: 32.5, per_location: {} });
  const canEditOmzet = ['owner', 'admin', 'multi_location'].includes(user?.role);
  const canAdjust = ['owner', 'admin', 'multi_location', 'single_location'].includes(user?.role);
  const [confirmOverwriteOpen, setConfirmOverwriteOpen] = useState(false);
  const [aiElapsed, setAiElapsed] = useState(0);
  const [bezettingsdoel, setBezettingsdoel] = useState({});
  const [adjustBusy, setAdjustBusy] = useState(null);
  const [copyBusy, setCopyBusy] = useState(false);
  const [publishStatus, setPublishStatus] = useState(null);
  const [publishBusy, setPublishBusy] = useState(false);
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [weekLock, setWeekLock] = useState(null);
  const undoTimerRef = useRef(null);
  const pollTimerRef = useRef(null);

  // Bootstrap: load eligible locations only.
  useEffect(() => {
    (async () => {
      const { data } = await api.get('/locations');
      const eligible = data.filter((l) => l.has_roster !== false);
      setLocations(eligible);
    })();
  }, []);

  // Fallback selection: if no location is set yet (no URL param, no sessionStorage),
  // default to the first eligible. Reads live `selectedSlug` (avoids closure-stale bug
  // that would overwrite a URL-set slug after the async fetch resolves).
  useEffect(() => {
    if (locations.length > 0 && !selectedSlug) {
      setSelectedSlug(locations[0].slug);
    }
  }, [locations, selectedSlug, setSelectedSlug]);

  const loadEmployees = useCallback(async () => {
    if (!selectedSlug) return;
    const { data } = await api.get(`/employees?location_slug=${selectedSlug}&status=actief`);
    setEmployees(data);
  }, [selectedSlug]);

  const loadWeek = useCallback(
    async ({ silent = false } = {}) => {
      if (!selectedSlug) return;
      try {
        const headers = silent && etag ? { 'If-None-Match': etag } : {};
        const resp = await api.get(
          `/assignments/week?location_id=${selectedSlug}&year=${year}&week_number=${week}`,
          { headers, validateStatus: (s) => (s >= 200 && s < 300) || s === 304 },
        );
        setLivePollOk(true);
        if (resp.status === 304) {
          setLastUpdate(new Date());
          return;
        }
        setAssignments(resp.data.assignments || []);
        setEtag(resp.headers.etag || '');
        setLastUpdate(new Date());
        if (silent && resp.data.assignments && resp.data.assignments.length > 0) {
          // Bijwerking gedetecteerd — toon subtiele toast
        }
      } catch (e) {
        setLivePollOk(false);
      }
    },
    [selectedSlug, year, week, etag],
  );

  useEffect(() => {
    loadEmployees();
    loadWeek();
    // loadEmployees/loadWeek are stable callbacks; we only want to react to slug/year/week changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSlug, year, week]);

  const loadPublishStatus = useCallback(async () => {
    if (!selectedSlug) return;
    try {
      const { data } = await api.get('/planner/roster-status', {
        params: { location_id: selectedSlug, year, week },
      });
      setPublishStatus(data);
    } catch {
      setPublishStatus(null);
    }
  }, [selectedSlug, year, week]);

  useEffect(() => {
    loadPublishStatus();
  }, [loadPublishStatus]);

  const loadWeekLock = useCallback(async () => {
    if (!selectedSlug) return;
    try {
      const { data } = await api.get('/planner/week-lock-status', {
        params: { location_id: selectedSlug, year, week },
      });
      setWeekLock(data);
    } catch {
      setWeekLock(null);
    }
  }, [selectedSlug, year, week]);

  useEffect(() => {
    loadWeekLock();
  }, [loadWeekLock]);

  function guardWeekLock() {
    if (weekLock?.locked) {
      setFeedback({ type: 'error', msg: weekLock.reason || 'Deze week is vergrendeld' });
      return true;
    }
    return false;
  }

  async function handlePublish() {
    if (!selectedSlug || publishBusy) return;
    setPublishBusy(true);
    try {
      const { data } = await api.post('/planner/publish-roster', {
        location_id: selectedSlug, year, week,
      });
      setFeedback({
        type: 'success',
        msg: data.no_op
          ? 'Geen wijzigingen sinds vorige publicatie — niets extra te melden'
          : `Rooster gepubliceerd — ${data.notified} medewerker(s) gemeld`,
      });
      await loadPublishStatus();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setFeedback({ type: 'error', msg: typeof detail === 'string' ? detail : 'Publiceren mislukt' });
    } finally {
      setPublishBusy(false);
      setPublishConfirmOpen(false);
    }
  }

  // Load AI usage status for cap-banner
  useEffect(() => {
    if (!selectedSlug) return;
    (async () => {
      try {
        const { data } = await api.get(`/ai/usage?location_id=${selectedSlug}`);
        setUsage(data);
      } catch (e) {
        setUsage(null);
      }
    })();
  }, [selectedSlug, aiResult]);

  /**
   * Gate function called by the "Genereer met AI" button. If the week
   * already contains shifts we first ask the user to confirm the
   * overwrite via an AlertDialog. Otherwise we kick off the generation
   * straight away. The "Confirm" branch on the dialog calls
   * `handleAIGenerate()` directly.
   */
  function requestAIGenerate() {
    if (!selectedSlug) return;
    if (assignments.length > 0) {
      setConfirmOverwriteOpen(true);
      return;
    }
    handleAIGenerate();
  }

  async function handleAIGenerate() {
    if (!selectedSlug) return;
    setAiGenerating(true);
    setAiResult(null);
    setAiFailedAt(null);
    setAiElapsed(0);
    setFeedback({ type: 'info', msg: 'AI genereert rooster\u2026 (kan tot 5 minuten duren)' });

    // ---- Async + polling pattern ----
    // The /api/ai/generate-week endpoint returns 202 + {job_id} immediately
    // (it spawns a background task). We poll /api/ai/jobs/{id} every 5s
    // until status is 'completed' or 'failed'. This sidesteps the
    // Emergent reverse-proxy's ~60s idle timeout that previously caused
    // 504 errors on long generations.
    const POLL_INTERVAL_MS = 5000;
    const MAX_POLL_MS = 6 * 60 * 1000; // safety: stop after 6 min (covers 1 placeholder-retry within 2x LLM_TIMEOUT_S)
    const startedAt = Date.now();
    let jobId = null;

    function handleResult(data) {
      setAiResult(data);
      // Defensive formatting: fallback paths may not provide confidence_overall
      // (e.g. template-fallback) or cost (when AI was not actually called).
      // Use Number.isFinite to guard against undefined/null/NaN values that
      // previously rendered as "0%" in the success banner.
      const conf = Number.isFinite(data?.confidence_overall)
        ? `${(data.confidence_overall * 100).toFixed(0)}%`
        : 'n.v.t.';
      const costCents = data?.usage?.cost_usd_cents;
      const costPart = Number.isFinite(costCents) && costCents > 0
        ? `, \u20ac${(costCents / 100 * 0.92).toFixed(3)}`
        : '';
      if (data.fallback_used) {
        setFeedback({
          type: 'error',
          msg: `AI tijdelijk niet bereikbaar \u2014 fallback gebruikt (${data.inserted || 0} shifts uit standaardrooster).`,
        });
      } else {
        setFeedback({
          type: 'success',
          msg: `AI klaar: ${data.inserted} nieuw, ${data.overwritten || 0} overschreven (confidence ${conf}${costPart})`,
        });
      }
    }

    function handleFailure(msg) {
      setFeedback({ type: 'error', msg: msg || 'AI-generatie mislukt' });
      setAiFailedAt(Date.now()); // enables the standaardrooster fallback button
    }

    try {
      // 1. Kick off the job — should return 202 within milliseconds
      const startResp = await api.post('/ai/generate-week', {
        location_id: selectedSlug, year, week, overwrite: true,
      }, { timeout: 15000 });

      jobId = startResp.data?.job_id;
      if (!jobId) {
        // Backwards-compat: if the backend ever falls back to a synchronous
        // response (no job_id present), treat the body itself as the result.
        if (startResp.data && (startResp.data.ok || startResp.data.inserted !== undefined)) {
          handleResult(startResp.data);
          await loadWeek();
          return;
        }
        throw new Error('Geen job_id ontvangen van de server');
      }

      // 2. Poll loop — bail out on completion, failure, or safety timeout
      while (true) {
        const elapsed = Date.now() - startedAt;
        if (elapsed > MAX_POLL_MS) {
          handleFailure(
            `AI-generatie duurt langer dan 6 minuten \u2014 server draait mogelijk nog door op de achtergrond. Probeer de pagina te verversen of gebruik de standaardrooster-fallback.`,
          );
          return;
        }

        // Update feedback every loop so the user sees progress (elapsed seconds)
        const seconds = Math.floor(elapsed / 1000);
        setAiElapsed(seconds);
        setFeedback({
          type: 'info',
          msg: `AI genereert rooster\u2026 (${seconds}s)`,
        });

        await new Promise((res) => setTimeout(res, POLL_INTERVAL_MS));

        let pollResp;
        try {
          pollResp = await api.get(`/ai/jobs/${jobId}`, { timeout: 10000 });
        } catch (pollErr) {
          // Transient network errors during polling: log + continue. A
          // permanent failure will be caught by the MAX_POLL_MS timeout.
          // eslint-disable-next-line no-console
          console.warn('[ai-poll] transient error, continuing:', pollErr?.message || pollErr);
          continue;
        }

        const job = pollResp.data;
        if (job.status === 'completed') {
          if (job.result) {
            handleResult(job.result);
            await loadWeek();
          } else {
            handleFailure('AI klaar, maar geen resultaat ontvangen.');
          }
          return;
        }
        if (job.status === 'failed') {
          handleFailure(job.error || 'AI-generatie mislukt op de server.');
          return;
        }
        // status is 'pending' or 'running' → keep polling
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : detail?.message || err.message || 'AI-generatie mislukt';
      handleFailure(msg);
    } finally {
      setAiGenerating(false);
    }
  }

  async function handleStandaardroosterFallback() {
    if (!selectedSlug || templateRolloutBusy) return;
    setTemplateRolloutBusy(true);
    setFeedback({ type: 'info', msg: 'Standaardrooster uitrollen\u2026' });
    try {
      const { data } = await api.post(
        `/templates/${selectedSlug}/rollout?year=${year}&week=${week}&overwrite=true`,
      );
      setFeedback({
        type: 'success',
        msg: `Standaardrooster gebruikt: ${data.inserted || 0} shifts toegevoegd${data.overwritten ? `, ${data.overwritten} overschreven` : ''}.`,
      });
      setAiFailedAt(null);
      await loadWeek();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : detail?.message
          || (err.response?.status === 404
            ? 'Nog geen standaardrooster opgeslagen voor deze locatie.'
            : 'Uitrollen standaardrooster mislukt.');
      setFeedback({ type: 'error', msg });
    } finally {
      setTemplateRolloutBusy(false);
    }
  }

  // Polling every 30s, pause on document hidden
  useEffect(() => {
    function startPoll() {
      if (pollTimerRef.current) return;
      pollTimerRef.current = setInterval(() => {
        if (!document.hidden) loadWeek({ silent: true });
      }, POLL_INTERVAL_MS);
    }
    function stopPoll() {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    }
    startPoll();
    function onVis() {
      if (document.hidden) stopPoll();
      else { startPoll(); loadWeek({ silent: true }); }
    }
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stopPoll();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [loadWeek]);

  const employeesById = useMemo(() => Object.fromEntries(employees.map((e) => [e.id, e])), [employees]);
  const weekDates = useMemo(() => isoWeekDates(year, week).map((d) => d.toISOString().slice(0, 10)), [year, week]);

  // Week-context t.o.v. vandaag: huidig / volgende / verleden / toekomst.
  const weekContext = useMemo(() => {
    const cur = getISOWeek();
    const nxt = getISOWeekOffset(1);
    if (year === cur.year && week === cur.week) return 'huidig';
    if (year === nxt.year && week === nxt.week) return 'volgende';
    const monday = weekDates?.[0];
    const todayIso = new Date().toISOString().slice(0, 10);
    if (monday && monday < todayIso) return 'verleden';
    return 'toekomst';
  }, [year, week, weekDates]);

  const WEEK_CTX = {
    huidig: { label: 'Huidige week', cls: 'bg-amber-100 text-amber-800' },
    volgende: { label: 'Volgende week', cls: 'bg-emerald-100 text-emerald-700' },
    verleden: { label: 'Verleden', cls: 'bg-rose-100 text-rose-700' },
    toekomst: { label: 'Toekomst', cls: 'bg-slate-100 text-slate-500' },
  };

  function findAssignment(empId, date) {
    return assignments.find((a) => a.employee_id === empId && a.date === date);
  }

  // Running total per employee
  const runningTotalById = useMemo(() => {
    const m = {};
    assignments.forEach((a) => {
      m[a.employee_id] = (m[a.employee_id] || 0) + (a.duration_net || 0);
    });
    return m;
  }, [assignments]);

  // Location-TOTAL planned hours this week (live — updates as the maker edits).
  const plannedTotal = useMemo(
    () => assignments.reduce((sum, a) => sum + (a.duration_net || 0), 0),
    [assignments],
  );

  // Loonkosten (Fase 2): per-employee kostprijs/uur snapshot for this location/
  // week; multiplied by the LIVE assignment hours so costs update on every edit.
  useEffect(() => {
    let cancelled = false;
    if (!selectedSlug) return;
    api.get('/loonkosten/rooster', { params: { location_id: selectedSlug, year, week } })
      .then(({ data }) => {
        if (cancelled) return;
        const m = {};
        (data.per_employee || []).forEach((e) => { if (e.kostprijs_per_uur != null) m[e.employee_id] = e.kostprijs_per_uur; });
        setKpuById(m);
      })
      .catch(() => { if (!cancelled) setKpuById({}); });
    return () => { cancelled = true; };
  }, [selectedSlug, year, week]);

  const fmtEUR = (n) => `€${Number(n || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // PK% (loonkosten / omzet) — configurable 4-tier band (DailyOps-aligned).
  const pkStatus = (pct, slug) => {
    if (pct == null) return 'unknown';
    const norm = pkDoel.per_location?.[slug] ?? pkDoel.default_pct ?? 32.5;
    const top = pkDoel.top_pct ?? (norm - 5);
    const kritiek = pkDoel.kritiek_pct ?? (norm + 5);
    if (pct < top) return 'gold';
    if (pct <= norm) return 'green';
    if (pct <= kritiek) return 'orange';
    return 'red';
  };
  const PK_TONE = {
    gold: 'bg-yellow-100 text-yellow-800',
    green: 'bg-emerald-100 text-emerald-700',
    orange: 'bg-orange-100 text-orange-700',
    red: 'bg-rose-100 text-rose-700',
    unknown: 'bg-slate-100 text-slate-500',
  };

  // Weersgebaseerde omzet-forecast per dag (Fase 4b): basis × weer × seizoen.
  // Bron per dag: handmatig > forecast > DailyOps > geen. Handmatig overschrijven kan.
  useEffect(() => {
    let cancelled = false;
    if (!selectedSlug) return;
    api.get('/dailyops/forecast', { params: { location_id: selectedSlug, year, week } })
      .then(({ data }) => {
        if (cancelled) return;
        const amounts = {}, sources = {}, detail = {};
        Object.entries(data.by_day || {}).forEach(([d, v]) => {
          if (v.amount != null) amounts[d] = v.amount;
          sources[d] = v.source;
          detail[d] = v;
        });
        setOmzetByDay(amounts);
        setOmzetSources(sources);
        setOmzetDetail(detail);
      })
      .catch(() => { if (!cancelled) { setOmzetByDay({}); setOmzetSources({}); setOmzetDetail({}); } });
    return () => { cancelled = true; };
  }, [selectedSlug, year, week]);

  // Effective-dated PK%-doel (item 3): resolve the norm that applies to the
  // planned week (norm-historie). Re-fetch when the week changes.
  useEffect(() => {
    let cancelled = false;
    const monday = weekDates?.[0];
    api.get('/loonkosten/pk-doel/resolve', { params: monday ? { date: monday } : {} })
      .then(({ data }) => { if (!cancelled) setPkDoel(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [weekDates]);

  // AI-planner v2 (F2): bezettingsdoel per open dag (forecast → wassingen → bezetting).
  useEffect(() => {
    let cancelled = false;
    if (!selectedSlug) return;
    api.get('/planner/bezettingsdoel', { params: { location_id: selectedSlug, year, week } })
      .then(({ data }) => { if (!cancelled) setBezettingsdoel(data.bezettingsdoel || {}); })
      .catch(() => { if (!cancelled) setBezettingsdoel({}); });
    return () => { cancelled = true; };
  }, [selectedSlug, year, week]);

  const saveOmzet = (dateIso, val) => {
    const amount = val === '' ? null : Number(val);
    // Snapshot vóór de optimistic update → terugdraaien bij een fout.
    const prevAmount = omzetByDay[dateIso];
    const prevSource = omzetSources[dateIso];
    setOmzetByDay((prev) => { const n = { ...prev }; if (amount == null) delete n[dateIso]; else n[dateIso] = amount; return n; });
    setOmzetSources((prev) => ({ ...prev, [dateIso]: amount == null ? 'geen' : 'handmatig' }));
    api.put('/omzet', { location_slug: selectedSlug, date: dateIso, amount }).catch(() => {
      // Rollback + NL-fouttoast (badge niet onterecht op 'Handmatig' laten staan).
      setOmzetByDay((prev) => { const n = { ...prev }; if (prevAmount == null) delete n[dateIso]; else n[dateIso] = prevAmount; return n; });
      setOmzetSources((prev) => ({ ...prev, [dateIso]: prevSource || 'geen' }));
      setFeedback({ type: 'error', msg: 'Omzet opslaan mislukt — vorige waarde hersteld. Probeer het opnieuw.' });
    });
  };

  const SOURCE_BADGE = {
    forecast: { label: 'Forecast', cls: 'bg-indigo-100 text-indigo-700' },
    dailyops: { label: 'DailyOps', cls: 'bg-sky-100 text-sky-700' },
    handmatig: { label: 'Handmatig', cls: 'bg-slate-200 text-slate-600' },
    geen: { label: '', cls: '' },
  };
  const sourceTitle = (d) => {
    const det = omzetDetail[d];
    if (!det || det.source !== 'forecast') return omzetSources[d] === 'dailyops' ? 'Bron: DailyOps target/actuals' : omzetSources[d] === 'handmatig' ? 'Handmatig ingevoerd' : '';
    const reason = { regen: 'regen −21%', inhaal: 'inhaalvraag +20%', neutraal: 'droog' }[det.weather_reason] || det.weather_reason;
    return `Forecast = basis €${det.base} × weer (${reason}, ×${det.weather_factor}) × seizoen ×${det.season_factor} · ${det.samples} weken historie`;
  };

  const plannedCostTotal = useMemo(
    () => assignments.reduce((sum, a) => sum + (a.duration_net || 0) * (kpuById[a.employee_id] || 0), 0),
    [assignments, kpuById],
  );
  const costByDay = useMemo(() => {
    const m = {};
    assignments.forEach((a) => { m[a.date] = (m[a.date] || 0) + (a.duration_net || 0) * (kpuById[a.employee_id] || 0); });
    return m;
  }, [assignments, kpuById]);
  const plannedHeadByDay = useMemo(() => {
    const m = {};
    assignments.forEach((a) => { m[a.date] = (m[a.date] || 0) + 1; });
    return m;
  }, [assignments]);
  const aiToetsByDate = useMemo(() => {
    const m = {};
    (aiResult?.f3?.applied || []).forEach((c) => { if (c.date) (m[c.date] = m[c.date] || []).push(c); });
    return m;
  }, [aiResult]);
  const omzetTotal = useMemo(() => Object.values(omzetByDay).reduce((s, v) => s + (Number(v) || 0), 0), [omzetByDay]);
  const weekPkPct = omzetTotal > 0 ? Math.round((plannedCostTotal / omzetTotal) * 1000) / 10 : null;

  function navigateWeek(delta) {
    const { year: y, week: w } = navigateIsoWeek(year, week, delta);
    setYear(y);
    setWeek(w);
    setEtag('');
  }

  async function createOrUpdate(empId, date, start, end, isHulpvraag, existing) {
    setFeedback({ type: '', msg: '' });
    try {
      if (existing) {
        const { data, headers } = await api.put(
          `/assignments/${existing.id}`,
          { start_time: start, end_time: end, is_hulpvraag: isHulpvraag },
          { headers: { 'If-Match': `"${existing.id}-${existing.version}"` } },
        );
        setAssignments((prev) => prev.map((a) => (a.id === data.id ? data : a)));
        setEditingCell(null);
      } else {
        const { data } = await api.post('/assignments', {
          location_id: selectedSlug,
          employee_id: empId,
          date,
          start_time: start,
          end_time: end,
          is_hulpvraag: isHulpvraag || false,
        });
        setAssignments((prev) => [...prev, data]);
        setEditingCell(null);
      }
      // Refresh ETag
      await loadWeek({ silent: false });
    } catch (e) {
      if (e.response?.status === 409) {
        setConflictNotice(true);
      } else {
        const detail = e.response?.data?.detail;
        setFeedback({ type: 'error', msg: typeof detail === 'string' ? detail : detail?.message || 'Opslaan mislukt' });
      }
    }
  }

  async function softDelete(assignment) {
    try {
      await api.delete(`/assignments/${assignment.id}`, {
        headers: { 'If-Match': `"${assignment.id}-${assignment.version}"` },
      });
      setAssignments((prev) => prev.filter((a) => a.id !== assignment.id));
      setEditingCell(null);
      // Setup undo state
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
      setUndoState({ assignment, expiresAt: Date.now() + UNDO_TOAST_MS });
      undoTimerRef.current = setTimeout(() => setUndoState(null), UNDO_TOAST_MS);
    } catch (e) {
      if (e.response?.status === 409) setConflictNotice(true);
      else setFeedback({ type: 'error', msg: 'Verwijderen mislukt' });
    }
  }

  async function undoDelete() {
    if (!undoState) return;
    try {
      if (undoState.adjust || undoState.copy) {
        const changes = (undoState.adjust || undoState.copy).changes;
        await api.post('/planner/adjust-undo', {
          location_id: selectedSlug, changes,
        });
        setUndoState(null);
        if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
        await loadWeek({ silent: false });
        setFeedback({ type: 'info', msg: undoState.copy ? 'Kopie ongedaan gemaakt.' : 'Aanpassing ongedaan gemaakt.' });
        return;
      }
      const { data } = await api.post(`/assignments/${undoState.assignment.id}/restore`);
      setAssignments((prev) => [...prev, data]);
      setUndoState(null);
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    } catch (e) {
      setFeedback({ type: 'error', msg: 'Herstellen mislukt' });
    }
  }

  async function handleAdjustToTarget(dateIso) {
    if (!selectedSlug || adjustBusy) return;
    setAdjustBusy(dateIso);
    try {
      const { data } = await api.post('/planner/adjust-to-target', {
        location_id: selectedSlug, year, week, date: dateIso,
      });
      await loadWeek({ silent: false });
      if (data.changed > 0) {
        if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
        setUndoState({ adjust: { date: dateIso, changes: data.changes }, expiresAt: Date.now() + UNDO_TOAST_MS });
        undoTimerRef.current = setTimeout(() => setUndoState(null), UNDO_TOAST_MS);
        setFeedback({ type: 'success', msg: `Dag aangepast naar doel: ${data.changed} diensten gewijzigd` });
      } else {
        setFeedback({ type: 'info', msg: data.reason || 'Niets aan te passen' });
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      setFeedback({ type: 'error', msg: typeof detail === 'string' ? detail : 'Aanpassen naar doel mislukt' });
    } finally {
      setAdjustBusy(null);
    }
  }

  async function handleCopyPreviousWeek() {
    if (!selectedSlug || copyBusy || weekContext === 'verleden') return;
    setCopyBusy(true);
    try {
      const { data } = await api.post('/planner/copy-previous-week', {
        location_id: selectedSlug, year, week,
      });
      await loadWeek({ silent: false });
      if (data.copied > 0) {
        if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
        setUndoState({ copy: { changes: data.changes }, expiresAt: Date.now() + UNDO_TOAST_MS });
        undoTimerRef.current = setTimeout(() => setUndoState(null), UNDO_TOAST_MS);
        setFeedback({
          type: 'success',
          msg: `${data.copied} diensten gekopieerd${data.skipped ? `, ${data.skipped} overgeslagen (verlof/bezet/niet inzetbaar)` : ''}`,
        });
      } else {
        setFeedback({ type: 'info', msg: data.reason || 'Niets te kopiëren' });
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      setFeedback({ type: 'error', msg: typeof detail === 'string' ? detail : 'Kopiëren mislukt' });
    } finally {
      setCopyBusy(false);
    }
  }

  return (
    <div className="space-y-4" data-testid="page-rooster">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <CalendarDays size={18} /> Roosterplanner
          </h2>
          <select
            value={selectedSlug}
            onChange={(e) => setSelectedSlug(e.target.value)}
            data-testid="select-location"
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm"
          >
            {locations.map((l) => (
              <option key={l.slug} value={l.slug}>{l.name}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => navigateWeek(-1)} data-testid="week-prev" className="rounded-md border border-slate-300 p-1.5 hover:bg-slate-50">
            <ChevronLeft size={14} />
          </button>
          <div className="rounded-md bg-slate-100 px-3 py-1.5 text-sm font-medium" data-testid="week-label">
            Week {week} / {year}
          </div>
          <span
            className={`rounded-full px-2 py-1 text-[11px] font-semibold ${WEEK_CTX[weekContext].cls}`}
            data-testid="week-context-badge"
          >
            {WEEK_CTX[weekContext].label}
          </span>
          <button onClick={() => navigateWeek(1)} data-testid="week-next" className="rounded-md border border-slate-300 p-1.5 hover:bg-slate-50">
            <ChevronRight size={14} />
          </button>
          {publishStatus && (
            <span
              className={`rounded-full px-2 py-1 text-[11px] font-semibold ${
                publishStatus.status === 'gepubliceerd'
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-slate-200 text-slate-600'}`}
              data-testid="publish-status-badge"
              title={publishStatus.published_at ? `Gepubliceerd: ${new Date(publishStatus.published_at).toLocaleString('nl-NL')}` : undefined}
            >
              {publishStatus.status === 'gepubliceerd' ? 'Gepubliceerd' : 'Concept'}
              {publishStatus.gewijzigd_na_publicatie && ' · gewijzigd na publicatie'}
            </span>
          )}
          {weekLock?.locked && (
            <span
              className="rounded-full bg-red-100 px-2 py-1 text-[11px] font-semibold text-red-700"
              data-testid="week-lock-badge"
              title={weekLock.reason}
            >
              Vergrendeld
            </span>
          )}
          {canAdjust && (
            <button
              onClick={() => setPublishConfirmOpen(true)}
              disabled={publishBusy || !selectedSlug || weekLock?.locked}
              data-testid="btn-publish-roster"
              title={weekLock?.locked ? weekLock.reason : 'Rooster publiceren — zichtbaar in het medewerkersportaal + melding aan ingeroosterde medewerkers'}
              className="flex items-center gap-1 rounded-md bg-brand-navy px-2 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <CalendarCheck size={12} /> {publishBusy ? 'Publiceren\u2026' : (publishStatus?.status === 'gepubliceerd' ? 'Herpubliceer' : 'Publiceer rooster')}
            </button>
          )}
          <button onClick={() => loadWeek()} className="flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1.5 text-xs hover:bg-slate-50" data-testid="btn-refresh-week">
            <RefreshCw size={12} /> Verversen
          </button>
          {canAdjust && (
            <button
              onClick={handleCopyPreviousWeek}
              disabled={copyBusy || weekContext === 'verleden'}
              data-testid="btn-copy-previous-week"
              title={weekContext === 'verleden'
                ? 'Verleden week — periode vergrendeld, kopiëren niet mogelijk'
                : 'Kopieer de geplande diensten van vorige week naar deze week (alleen lege cellen)'}
              className="flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1.5 text-xs hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <CalendarDays size={12} /> {copyBusy ? 'Kopiëren\u2026' : 'Kopieer vorige week'}
            </button>
          )}
          <Link
            to={`/beschikbaarheid?location=${selectedSlug || ''}`}
            data-testid="btn-availability-link"
            className="flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1.5 text-xs hover:bg-slate-50"
            title="Beschikbaarheid bewerken"
          >
            <CalendarCheck size={12} /> Beschikbaarheid
          </Link>
          <button
            onClick={requestAIGenerate}
            disabled={aiGenerating || usage?.caps?.has_ai === false || (usage?.usage?.cost_eur >= usage?.caps?.monthly_cap_eur)}
            data-testid="btn-ai-generate"
            className="flex items-center gap-1.5 rounded-md bg-brand-amber px-3 py-1.5 text-sm font-medium text-brand-navy hover:bg-brand-amber-dark disabled:opacity-50 shadow-sm"
            title={usage?.caps?.has_ai === false ? 'Geen AI op deze locatie' : ''}
          >
            <Sparkles size={14} /> {aiGenerating ? 'AI bezig\u2026' : 'Genereer met AI'}
          </button>
          <button
            onClick={() => setChatOpen((v) => !v)}
            data-testid="btn-ai-chat"
            className="flex items-center gap-1 rounded-md border border-brand-navy px-2 py-1.5 text-xs text-brand-navy hover:bg-brand-navy/10"
          >
            <MessageSquare size={12} /> Chat
          </button>
          <div className="flex items-center gap-1 text-[10px] text-slate-500">
            {livePollOk ? <Wifi size={11} className="text-green-600" /> : <WifiOff size={11} className="text-red-600" />}
            {lastUpdate ? lastUpdate.toLocaleTimeString('nl-NL') : ''}
          </div>
        </div>
      </div>

      {(weekContext === 'huidig' || weekContext === 'verleden') && (
        <div
          className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800"
          data-testid="week-edit-warning"
        >
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>
            {weekContext === 'huidig'
              ? 'Let op: je bewerkt de huidige week. Wijzigingen kunnen reeds gewerkte of goedgekeurde diensten raken — plan bij voorkeur vooruit.'
              : 'Let op: je bewerkt een week in het verleden. Deze week is doorgaans al verwerkt; wijzig alleen bewust.'}
          </span>
        </div>
      )}

      <RosterBudgetBar locationSlug={selectedSlug} year={year} week={week} plannedTotal={plannedTotal} />

      {/* Geplande loonkosten (Fase 2) + PK%-indicatie (Fase 3) — live tegen het rooster */}
      <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 space-y-2" data-testid="roster-cost-summary">
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <span className="flex items-center gap-2 font-medium text-slate-700">
            <Gauge size={15} /> Geplande loonkosten week{' '}
            <strong className="text-brand-navy tabular-nums" data-testid="roster-cost-week-total">{fmtEUR(plannedCostTotal)}</strong>
          </span>
          <span className="flex items-center gap-2 text-xs text-slate-500">
            Verwachte omzet week <strong className="tabular-nums text-slate-700" data-testid="roster-omzet-week-total">{fmtEUR(omzetTotal)}</strong>
            {weekPkPct != null && (
              <span className={`rounded-full px-2 py-0.5 font-medium ${PK_TONE[pkStatus(weekPkPct, selectedSlug)]}`} data-testid="roster-week-pk">
                PK% {weekPkPct}%
              </span>
            )}
          </span>
        </div>
        <p className="text-[11px] text-slate-400" data-testid="roster-forecast-explain">
          Verwachte omzet wordt automatisch geschat (forecast = historisch weekdag-gemiddelde × weer × seizoen) of uit DailyOps gehaald. Je kunt elke dag handmatig overschrijven.
        </p>
        <div className="grid grid-cols-7 gap-2">
          {DAYS.map((d, i) => {
            const dateIso = weekDates[i];
            const dayCost = costByDay[dateIso] || 0;
            const dayOmzet = omzetByDay[dateIso];
            const dayPk = dayOmzet ? Math.round((dayCost / dayOmzet) * 1000) / 10 : null;
            const src = omzetSources[dateIso];
            const badge = SOURCE_BADGE[src];
            return (
              <div key={d.dow} className="rounded-md border border-slate-100 px-2 py-1.5 text-center" data-testid={`roster-cost-day-${dateIso}`}>
                <div className="text-[10px] uppercase text-slate-400" data-testid={`roster-cost-day-header-${dateIso}`}>
                  {d.short} {dateIso ? `${dateIso.slice(8, 10)}-${dateIso.slice(5, 7)}` : ''}
                </div>
                <div className="text-xs font-medium text-slate-700 tabular-nums">{fmtEUR(dayCost)}</div>
                {canEditOmzet ? (
                  <input
                    key={`${dateIso}-${dayOmzet ?? 'x'}`}
                    type="number" min="0" step="1"
                    defaultValue={dayOmzet ?? ''}
                    onBlur={(e) => saveOmzet(dateIso, e.target.value)}
                    data-testid={`roster-omzet-input-${dateIso}`}
                    placeholder="omzet €"
                    className="mt-1 w-full rounded border border-slate-200 px-1 py-0.5 text-[11px] text-right"
                  />
                ) : (
                  <div className="mt-1 text-[10px] text-slate-400">{dayOmzet ? fmtEUR(dayOmzet) : '—'}</div>
                )}
                {badge && badge.label && (
                  <div className={`mt-1 rounded px-1 py-0.5 text-[9px] font-medium ${badge.cls}`}
                    title={sourceTitle(dateIso)} data-testid={`roster-omzet-source-${dateIso}`}>
                    {badge.label}
                  </div>
                )}
                {omzetDetail[dateIso]?.voorlopig && (
                  <div className="mt-1 rounded px-1 py-0.5 text-[9px] font-medium bg-violet-100 text-violet-700"
                    title="Weer nog niet bekend — schatting op basis × seizoen; verfijnt automatisch zodra de dag binnen de ~16-daagse weersvoorspelling valt."
                    data-testid={`roster-voorlopig-${dateIso}`}>
                    Voorlopig
                  </div>
                )}
                {dayPk != null && (
                  <div className={`mt-1 rounded px-1 py-0.5 text-[10px] font-medium ${PK_TONE[pkStatus(dayPk, selectedSlug)]}`} data-testid={`roster-day-pk-${dateIso}`}>
                    {dayPk}%
                  </div>
                )}
                {bezettingsdoel[dateIso] && (() => {
                  const tgt = bezettingsdoel[dateIso].headcount_target;
                  const mn = bezettingsdoel[dateIso].min_hc;
                  const plannedH = plannedHeadByDay[dateIso] || 0;
                  const tone = plannedH > tgt ? 'bg-rose-100 text-rose-700' : plannedH < mn ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-500';
                  return (
                    <div className={`mt-1 flex items-center justify-center gap-0.5 rounded px-1 py-0.5 text-[9px] font-medium ${tone}`}
                      title={`Bezettingsdoel ${tgt} (min ${mn}) · gepland ${plannedH}`} data-testid={`roster-bezetting-${dateIso}`}>
                      <Users size={9} /> {plannedH}/{tgt}
                    </div>
                  );
                })()}
                {bezettingsdoel[dateIso] && canAdjust && (() => {
                  const tgt = bezettingsdoel[dateIso].headcount_target;
                  const plannedH = plannedHeadByDay[dateIso] || 0;
                  if (plannedH === tgt) return null;
                  const dayShifts = assignments.filter((a) => a.date === dateIso);
                  const hasPlanned = dayShifts.some((a) => a.status === 'planned');
                  const over = plannedH > tgt;
                  const locked = over && !hasPlanned; // niets verwijderbaar → periode vergrendeld
                  return (
                    <button
                      disabled={locked || adjustBusy === dateIso}
                      onClick={() => handleAdjustToTarget(dateIso)}
                      title={locked
                        ? 'Periode vergrendeld — alleen goedgekeurde/gewerkte diensten op deze dag'
                        : `Pas de bezetting van deze dag in één klik aan naar het doel (${tgt})`}
                      data-testid={`btn-adjust-target-${dateIso}`}
                      className="mt-1 w-full rounded border border-brand-navy/30 bg-white px-1 py-0.5 text-[9px] font-medium text-brand-navy transition-colors hover:bg-brand-navy/5 disabled:cursor-not-allowed disabled:opacity-40">
                      {adjustBusy === dateIso ? '…' : 'Pas aan naar doel'}
                    </button>
                  );
                })()}
                {(aiToetsByDate[dateIso] || []).length > 0 && (
                  <div className="mt-1 rounded px-1 py-0.5 text-[9px] font-medium bg-indigo-100 text-indigo-700 flex items-center justify-center gap-0.5"
                    title={(aiToetsByDate[dateIso] || []).map((c) => `${c.action}: ${c.reden || ''}`).join(' · ')}
                    data-testid={`roster-aitoets-${dateIso}`}>
                    <Sparkles size={9} /> AI ×{(aiToetsByDate[dateIso] || []).length}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {usage && usage.caps?.monthly_cap_eur && usage.usage?.cost_eur >= 0.8 * usage.caps.monthly_cap_eur && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-center gap-2" data-testid="ai-cap-warning">
          <AlertTriangle size={14} />
          AI-budget voor {selectedSlug} bijna op: \u20ac{usage.usage.cost_eur} van \u20ac{usage.caps.monthly_cap_eur} gebruikt ({((usage.usage.cost_eur / usage.caps.monthly_cap_eur) * 100).toFixed(0)}%).
        </div>
      )}

      {conflictNotice && (
        <div className="flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="conflict-notice">
          <AlertTriangle size={16} className="mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold">Dit rooster is door een collega gewijzigd.</p>
            <p className="text-xs mt-0.5">Vernieuw de pagina om de laatste versie te zien voor je verder gaat.</p>
          </div>
          <button onClick={() => { setConflictNotice(false); loadWeek(); }} data-testid="btn-conflict-refresh"
            className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700">
            Vernieuwen
          </button>
        </div>
      )}

      {feedback.msg && (
        <div
          className={`rounded-md px-3 py-2 text-sm ${
            feedback.type === 'error'
              ? 'bg-red-50 text-red-700'
              : feedback.type === 'info'
                ? 'bg-amber-50 text-amber-800'
                : 'bg-green-50 text-green-700'
          }`}
          data-testid="ai-feedback"
        >
          <div className="flex items-start justify-between gap-3">
            <span className="flex-1">{feedback.msg}</span>
            {feedback.type === 'error' && aiFailedAt && (
              <button
                type="button"
                onClick={handleStandaardroosterFallback}
                disabled={templateRolloutBusy || weekLock?.locked}
                data-testid="btn-fallback-standaardrooster"
                className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-brand-navy px-3 py-1 text-xs font-medium text-white hover:bg-brand-navy-light disabled:opacity-60"
                title={weekLock?.locked ? weekLock.reason : 'Rolt het opgeslagen standaardrooster van deze locatie uit naar deze week'}
              >
                {templateRolloutBusy ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Grid3X3 size={12} />
                )}
                Gebruik standaardrooster
              </button>
            )}
          </div>
        </div>
      )}

      {aiGenerating && (
        <div className="rounded-lg border border-brand-amber/40 bg-amber-50/60 px-4 py-3" data-testid="ai-progress-steps">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            {[
              { k: 0, label: 'Rooster bouwen' },
              { k: 1, label: 'Drukte & kosten' },
              { k: 2, label: 'AI-toets' },
            ].map((s) => {
              const cur = aiElapsed < 3 ? 0 : aiElapsed < 8 ? 1 : 2;
              const done = s.k < cur;
              const active = s.k === cur;
              return (
                <div key={s.k} className="flex items-center gap-1.5" data-testid={`ai-step-${s.k}`}>
                  {done ? <CheckCircle2 size={14} className="text-emerald-600" />
                    : active ? <Loader2 size={14} className="animate-spin text-brand-amber-dark" />
                    : <Circle size={14} className="text-slate-300" />}
                  <span className={active ? 'font-semibold text-brand-navy' : done ? 'text-emerald-700' : 'text-slate-400'}>{s.label}</span>
                </div>
              );
            })}
            <span className="ml-auto text-xs text-slate-500 tabular-nums">{aiElapsed}s</span>
          </div>
        </div>
      )}

      {aiResult?.f3?.used_llm && (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 px-4 py-3 space-y-1.5" data-testid="ai-toets-summary">
          <div className="flex items-center gap-2 text-sm font-medium text-indigo-900">
            <Sparkles size={15} /> AI-toets
            <span className="text-xs font-normal text-indigo-700">
              {(aiResult.f3.applied || []).length} aanpassing{(aiResult.f3.applied || []).length === 1 ? '' : 'en'} · {(aiResult.f3.rejected || []).length} geweigerd
            </span>
          </div>
          {aiResult.f3.week_overview && (
            <p className="text-xs text-indigo-800 leading-snug" data-testid="ai-toets-overview">{aiResult.f3.week_overview}</p>
          )}
          {(aiResult.f3.applied || []).length > 0 && (
            <ul className="text-[11px] text-indigo-700 space-y-0.5">
              {(aiResult.f3.applied || []).slice(0, 6).map((c, i) => (
                <li key={`f3-${i}`}>• {({ vul_gat: 'toegevoegd', verplaats: 'verplaatst', wissel: 'gewisseld', verwijder: 'verwijderd' }[c.action] || c.action)} — {c.date}{c.reden ? `: ${c.reden}` : ''}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left text-xs uppercase tracking-wider text-slate-500 min-w-[200px] sticky left-0 bg-slate-50 z-10">Medewerker</th>
                {DAYS.map((d, i) => (
                  <th key={d.dow} className="px-2 py-2 text-center text-xs uppercase tracking-wider text-slate-500 min-w-[110px]">
                    <div>{d.short}</div>
                    <div className="text-[10px] font-normal text-slate-400">{formatDateNL(weekDates[i])}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {employees.length === 0 ? (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-sm text-slate-500">Geen actieve medewerkers op deze locatie</td></tr>
              ) : employees.map((emp) => {
                const total = runningTotalById[emp.id] || 0;
                const planned = total > 0;
                const contractH = emp.contract_hours || 0;
                const isOver = total > contractH * 1.1;
                return (
                  <tr key={emp.id} className="border-t border-slate-100 hover:bg-slate-50/30">
                    <td className="px-3 py-1.5 sticky left-0 bg-white border-r border-slate-100" data-testid={`r-row-${emp.personeelsnummer}`}>
                      <p className="text-sm font-medium text-slate-900 truncate">{emp.name}</p>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-slate-500">{emp.role} · {contractH}u</p>
                        {planned && (
                          <span className={`text-xs tabular-nums ${isOver ? 'text-red-600 font-semibold' : 'text-slate-700'}`}>
                            → {formatHoursHM(total)}
                            {kpuById[emp.id] != null && (
                              <span className="text-slate-400"> · {fmtEUR(total * kpuById[emp.id])}</span>
                            )}
                          </span>
                        )}
                      </div>
                    </td>
                    {DAYS.map((d, i) => {
                      const dateIso = weekDates[i];
                      const a = findAssignment(emp.id, dateIso);
                      const isEditing = editingCell?.empId === emp.id && editingCell?.date === dateIso;
                      return (
                        <td key={d.dow} className="p-1 border-r border-slate-100 align-middle">
                          {isEditing ? (
                            <RosterCellEditor
                              initial={a}
                              onSave={(s, e, h) => createOrUpdate(emp.id, dateIso, s, e, h, a)}
                              onDelete={() => a && softDelete(a)}
                              onCancel={() => setEditingCell(null)}
                            />
                          ) : a ? (
                            <div className="relative w-full">
                              <button
                                type="button"
                                onClick={() => { if (!guardWeekLock()) setEditingCell({ empId: emp.id, date: dateIso }); }}
                                data-testid={`r-cell-${emp.personeelsnummer}-${dateIso}`}
                                title={weekLock?.locked ? weekLock.reason : undefined}
                                className={`relative w-full rounded p-1 text-center text-xs font-medium hover:opacity-90 ${weekLock?.locked ? 'opacity-50 cursor-not-allowed' : ''} ${
                                  a.is_hulpvraag
                                    ? 'border-2 border-red-500 bg-red-50 text-red-900'
                                    : a.duration_net >= 10 ? 'bg-shift-full text-white'
                                    : d.dow === 6 ? 'bg-shift-weekend' : 'bg-shift-day text-slate-800'
                                }`}
                              >
                                {a.is_hulpvraag && <Flag size={9} className="absolute top-0.5 right-0.5" />}
                                <p className="tabular-nums">{a.start_time}–{a.end_time}</p>
                                <p className="text-[10px] opacity-80">{formatHoursHM(a.duration_net)}{kpuById[emp.id] != null ? ` · ${fmtEUR((a.duration_net || 0) * kpuById[emp.id])}` : ''}</p>
                              </button>
                              {a.source === 'ai_generated' && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setReasoningOpen({ assignmentId: a.id, anchorRect: e.currentTarget.getBoundingClientRect() });
                                  }}
                                  data-testid={`r-cell-sparkle-${emp.personeelsnummer}-${dateIso}`}
                                  className="absolute top-0 left-0 rounded bg-brand-amber text-brand-navy p-0.5 shadow"
                                  title={`AI (confidence ${((a.confidence || 0) * 100).toFixed(0)}%) - klik voor uitleg`}
                                >
                                  <Sparkles size={9} />
                                </button>
                              )}
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => { if (!guardWeekLock()) setEditingCell({ empId: emp.id, date: dateIso }); }}
                              data-testid={`r-cell-${emp.personeelsnummer}-${dateIso}`}
                              title={weekLock?.locked ? weekLock.reason : undefined}
                              className={`w-full rounded border border-dashed border-slate-200 px-1 py-2 text-xs text-slate-300 hover:border-brand-amber hover:text-brand-amber ${weekLock?.locked ? 'opacity-40 cursor-not-allowed' : ''}`}
                            >+</button>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {undoState && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 rounded-full bg-slate-900 px-5 py-2.5 text-sm text-white shadow-2xl" data-testid="undo-toast">
          {undoState.adjust
            ? `Dag aangepast naar doel: ${undoState.adjust.changes.length} diensten gewijzigd`
            : undoState.copy
              ? `Vorige week gekopieerd: ${undoState.copy.changes.length} diensten`
              : 'Shift verwijderd'}
          <button onClick={undoDelete} data-testid="btn-undo"
            className="flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-xs font-medium hover:bg-white/20">
            <Undo2 size={12} /> Ongedaan maken
          </button>
        </div>
      )}

      <AIChatPanel
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        locationSlug={selectedSlug}
        year={year}
        week={week}
        cellContext={chatContext}
        onCellContextClear={() => setChatContext(null)}
      />

      {reasoningOpen && (
        <ReasoningPopover
          open
          assignmentId={reasoningOpen.assignmentId}
          anchorRect={reasoningOpen.anchorRect}
          onClose={() => setReasoningOpen(null)}
        />
      )}

      <AlertDialog open={confirmOverwriteOpen} onOpenChange={setConfirmOverwriteOpen}>
        <AlertDialogContent data-testid="ai-overwrite-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Bestaande shifts overschrijven?</AlertDialogTitle>
            <AlertDialogDescription>
              Er {assignments.length === 1 ? 'staat' : 'staan'} al{' '}
              <strong>{assignments.length} shift{assignments.length === 1 ? '' : 's'}</strong>{' '}
              in week <strong>{week}/{year}</strong>. Wil je deze overschrijven met AI-gegenereerde shifts?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ai-overwrite-cancel">Annuleren</AlertDialogCancel>
            <AlertDialogAction
              data-testid="ai-overwrite-confirm"
              onClick={() => {
                setConfirmOverwriteOpen(false);
                handleAIGenerate();
              }}
              className="bg-brand-amber text-brand-navy hover:bg-brand-amber-dark"
            >
              Overschrijven
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={publishConfirmOpen} onOpenChange={setPublishConfirmOpen}>
        <AlertDialogContent data-testid="publish-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {publishStatus?.status === 'gepubliceerd' ? 'Rooster herpubliceren?' : 'Rooster publiceren?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Week <strong>{week}/{year}</strong> wordt zichtbaar in het medewerkersportaal en alle
              ingeroosterde medewerkers krijgen de melding "nieuw rooster staat klaar"
              {publishStatus?.status === 'gepubliceerd' ? ' (opnieuw, voor deze publicatie-actie).' : '.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="publish-confirm-cancel">Annuleren</AlertDialogCancel>
            <AlertDialogAction
              data-testid="publish-confirm-submit"
              onClick={handlePublish}
              className="bg-brand-navy text-white hover:opacity-90"
            >
              {publishStatus?.status === 'gepubliceerd' ? 'Herpubliceer' : 'Publiceer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function RosterCellEditor({ initial, onSave, onDelete, onCancel }) {
  const [start, setStart] = useState(initial?.start_time || '08:00');
  const [end, setEnd] = useState(initial?.end_time || '17:00');
  const [hulpvraag, setHulpvraag] = useState(initial?.is_hulpvraag || false);
  const valid = end > start;

  return (
    <div className="rounded-md border-2 border-brand-amber bg-white p-1.5 space-y-1" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-1">
        <input type="time" value={start} onChange={(e) => setStart(e.target.value)} data-testid="r-cell-start"
          className="w-full rounded border border-slate-300 px-1 py-0.5 text-xs" />
        <span className="text-xs">–</span>
        <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="r-cell-end"
          className="w-full rounded border border-slate-300 px-1 py-0.5 text-xs" />
      </div>
      <label className="flex items-center gap-1 text-[10px] text-slate-700">
        <input type="checkbox" checked={hulpvraag} onChange={(e) => setHulpvraag(e.target.checked)} data-testid="r-cell-hulpvraag" />
        Hulpvraag
      </label>
      <div className="flex justify-between gap-1">
        {initial && (
          <button type="button" onClick={onDelete} data-testid="r-cell-delete"
            className="rounded p-1 text-red-600 hover:bg-red-50"><Trash2 size={11} /></button>
        )}
        <div className="flex gap-1 ml-auto">
          <button type="button" onClick={onCancel} className="rounded px-1.5 py-0.5 text-[10px] text-slate-600 hover:bg-slate-100">Annu</button>
          <button type="button" disabled={!valid} onClick={() => valid && onSave(start, end, hulpvraag)} data-testid="r-cell-save"
            className="rounded bg-brand-navy px-1.5 py-0.5 text-[10px] font-medium text-white disabled:opacity-40">OK</button>
        </div>
      </div>
    </div>
  );
}


const fmtU = (h) => `${Number(h || 0).toFixed(2).replace(/\.00$/, '')}u`;

function RosterBudgetBar({ locationSlug, year, week, plannedTotal }) {
  const [budget, setBudget] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    if (!locationSlug) return;
    api.get('/roster-guard', { params: { location_id: locationSlug, year, week } })
      .then(({ data }) => { if (!cancelled) setBudget(data.budget || null); })
      .catch(() => { if (!cancelled) setBudget(null); });
    return () => { cancelled = true; };
  }, [locationSlug, year, week]);

  if (!budget || !budget.enabled) return null;

  const { min, max } = budget;
  // Live status from the maker's current plan (not the server snapshot).
  let status = 'ok';
  if (max != null && plannedTotal > max) status = 'over';
  else if (min != null && plannedTotal < min) status = 'under';

  const tone = status === 'over'
    ? 'border-rose-300 bg-rose-50 text-rose-800'
    : status === 'under'
      ? 'border-amber-300 bg-amber-50 text-amber-900'
      : 'border-slate-200 bg-white text-slate-700';
  const barTone = status === 'over' ? 'bg-rose-500' : status === 'under' ? 'bg-amber-500' : 'bg-emerald-500';
  const scaleMax = Math.max(max || 0, plannedTotal, 1);
  const pct = Math.min((plannedTotal / scaleMax) * 100, 100);
  const minPct = min != null ? Math.min((min / scaleMax) * 100, 100) : null;
  const maxPct = max != null ? Math.min((max / scaleMax) * 100, 100) : null;

  return (
    <div data-testid="roster-budget-bar" className={`rounded-lg border px-3 py-2.5 ${tone}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <span className="flex items-center gap-2 font-medium">
          <Gauge size={15} />
          Gepland <strong data-testid="roster-budget-total">{fmtU(plannedTotal)}</strong> · budget {min != null ? fmtU(min) : '—'}–{max != null ? fmtU(max) : '—'}
          {budget.planned_cost_eur != null && (
            <span className="text-slate-500 font-normal" data-testid="roster-budget-cost"> · €{Number(budget.planned_cost_eur).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} loonkosten</span>
          )}
        </span>
        {status === 'over' && (
          <span data-testid="roster-budget-warning-over" className="flex items-center gap-1 rounded bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700">
            <AlertTriangle size={12} /> Boven max (+{fmtU(plannedTotal - max)})
          </span>
        )}
        {status === 'under' && (
          <span data-testid="roster-budget-warning-under" className="flex items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
            <AlertTriangle size={12} /> Onder min ({fmtU(min - plannedTotal)} te weinig)
          </span>
        )}
      </div>
      <div className="relative mt-2 h-2 w-full rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full ${barTone}`} style={{ width: `${pct}%` }} />
        {minPct != null && <span className="absolute top-0 h-full w-px bg-slate-400/70" style={{ left: `${minPct}%` }} title="min" />}
        {maxPct != null && <span className="absolute top-0 h-full w-px bg-slate-600/80" style={{ left: `${maxPct}%` }} title="max" />}
      </div>
    </div>
  );
}
